//cropImageViewPort.js?v=6.0.43
import imageHelper from './imageHelper.js';
import products from './products.js';
import Storage from './storage.js';
import events from './event.js';
import { imageUrl, textString, getNumber, buttonString } from './texts.js';
import Upload from './upload.js';
import Logger from './logger.js';
import _ from './translate.js';
import ThumbnailCompositor from './ThumbnailCompositor.js';
import { renderCurrentGraphic } from './textStep.js';
import db from './db.js';
import imageTokens from './imageTokens.js';
import CoverDialog from './coverDialog.js';
import FrameZoomView from './FrameZoomView.js';

class CropImageViewport {
    wrapper = null;
    cropcontainer = null;
    stack = null;
    img = null;
    size = '40x50';
    aspectRatio = 1;
    scale = 1;
    minScale = 1;
    maxScale = 3;
    pos = { x: 0, y: 0 };
    start = {};
    dragging = false;
    pinchStart = null;
    upload = null; // Instance of the Upload class
    options = {};

    constructor(wrapper, options = {}) {
        if (!wrapper) throw new Error('Wrapper element is required');
        this.wrapper = wrapper;
        this.options = options;
        this.init();
    }

    async init() {
        await this.load();

        // const maxZoom = parseFloat(window?.mscp?.settings?.maxZoom);
        // if (!isNaN(maxZoom)) {
        //     this.maxScale = maxZoom;
        // }

        this.attachEvents();
    }

    async load() {
        this.wrapper.innerHTML = '';
        this.loadWrappers();
        this.addEnlargeButton();
        this.addListeners();
        await this.updateOnLoad();
        this.showThumbnail();
    }

    /**
     * "Förstora"-knapp i customizern (spec entry 1): visar den inramade tavlan
     * i fullskärm med pan/zoom. Klonar den levande stacken 1:1 så vyn matchar
     * exakt vad customizern renderar (rätt ramlager + full-res foto + crop).
     * Knappen visas endast när ett foto är laddat.
     */
    addEnlargeButton() {
        if (this.enlargeButton) return;
        const btn = document.createElement('span');
        btn.className = 'mscp-enlarge-button mscp-floating-button';
        btn.title = buttonString('enlarge_button', _('Enlarge'));
        btn.innerHTML = (window.mindstore_object?.icon?.search) || '⤢';
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!this.img || !this.img.naturalWidth) return; // inget foto ännu
            FrameZoomView.open({ source: 'clone', stack: this.stack });
        });
        this.wrapper.appendChild(btn);
        this.enlargeButton = btn;

        // Visa knappen först när ett foto finns
        const sync = () => this.wrapper.classList.toggle(
            'mscp-has-photo', !!(this.img && this.img.naturalWidth)
        );
        events.add('imageLoaded', () => requestAnimationFrame(sync));
        events.add('newImageLocalSaved', () => this.wrapper.classList.remove('mscp-has-photo'));
        sync();
    }

    addListeners() {
        events.add('reInitUpload', async () => {
            this.previewcontainer?.remove();
            this.file = null;
            await this.initUpload();
        });
        // events.add('changeStep', async (step) => {
        //     if (step === 'upload') {
        //         // this.cleanupAnimation();
        //         // await this.loadActiveImage();
        //     }
        // });

        events.add('updateCropSize', async (force = false) => {
            if (force) {
                Storage.remove('cropBox'); // Reset cropBox to force recalculation
            }
            await this.updateCropSize();
        });

        // Nollställ frame vid paper-byte så att default-frame för nytt paper används
        events.add('setPaperSelection', () => {
            Storage.remove('frame');
        });
        // events.add('popupResize', () => {
        //     this.updateCropSize();
        // }, { throttle: 300 });
        // events.add('imageLoaded', () => {
        //     console.log('imageLoaded event received in CropImageViewport');
        //     this.updateCropSize();
        // });
        // Synka stack-dataset (data-frame m.m.) vid stegbyte så att
        // visuellt state matchar Storage även när man backar
        events.add('stepChanged', async (step) => {
            if (['paper', 'presentation', 'size'].includes(step)) {
                await this.updateCropSize();
            }
        });

        events.add('newImageLocalSaved', async ({ status }) => {
            this.previewcontainer?.remove();
            this.file = null;
            // reset cropBox
            // if (status !== 'converting') {
            Storage.remove('cropBox');
            // }
            await this.loadActiveImage();
        });

        // if (this.options.loadUploadFunction) { // Load the upload functionality if specified in options
        // events.add('imageUploadSuccess', async ({ id, url }) => {
        //     this.img.src = url;
        //     this.img.onload = () => {
        //         this.img.width = this.img.naturalWidth;
        //         this.img.height = this.img.naturalHeight;
        //         events.trigger('imageLoaded');
        //     };
        //     this.img.onerror = () => {
        //         this.img.remove();
        //         Logger.error('Error loading image. Please try again (%s)', id);
        //         // this.wrapper.innerHTML = '<p>Error loading image. Please try again.</p>';
        //     };
        // });
        events.add('showFileUpload', async (triggerClick = false) => {
            // events.trigger('changeStep', 'upload'); // Trigger change step to upload
            await this.initUpload(triggerClick);
        });

        // events.add('cleanupBeforeNewImage', async () => {
        //     this.reCreateImg();
        //     this.addMessage('info', textString('upload_new_image_uploading_info'));
        // });

        // // }

        /// update crop size after image is loaded and rendered
        events.add('imageLoaded', async () => {
            Logger.debug('imageLoaded event received. Deferring updateCropSize to next paint frame.');
            // wait for next paint frame
            requestAnimationFrame(async () => {
                // wait for another paint frame to be sure
                requestAnimationFrame(async () => {
                    await this.updateCropSize();
                });
            });
        });

        events.add('imageUploadStatusChanged', async ({ id, newStatus, serverUrl }) => {
            const activeImageID = Storage.get('activeImageID');
            // Se till att vi har en serverUrl att byta till
            if (id == activeImageID && newStatus === 'uploaded' && serverUrl) {
                Logger.debug('CropImageViewport: Bilden uppladdad. Kör flimmerfri uppdatering.');
                await this.updateImageSrc(serverUrl);
            }
        });

        events.add('setStackDataset', (layer, value) => {
            this.stack.dataset[layer] = value;
        });
        this.addSaveThumbnailEvents();
    }

    async initUpload(triggerClick = false) {
        // Prevent multiple simultaneous calls
        if (this._initUploadInProgress) {
            Logger.debug('initUpload already in progress, skipping');
            return;
        }
        this._initUploadInProgress = true;

        try {
            if (!this.upload) {
                this.upload = new Upload(this.wrapper, triggerClick);
                await this.upload.init();
            }
            else {
                await this.upload.reload();
                if (triggerClick) {
                    this.upload.triggerChange();
                }
            }
        } finally {
            this._initUploadInProgress = false;
        }
    }

    loadWrappers() {

        this.stack = document.createElement('div');
        this.stack.className = 'mscp-stack';
        this.wrapper.appendChild(this.stack);

        const wall = document.createElement('div');
        wall.className = 'mscp-layer wall';
        this.stack.appendChild(wall);

        const shadow = document.createElement('div');
        shadow.className = 'mscp-layer shadow';
        this.stack.appendChild(shadow);

        const photoWrapper = document.createElement('div');
        photoWrapper.className = 'mscp-photo-wrapper';

        this.cropcontainer = document.createElement('div');
        this.cropcontainer.className = 'crop-container';
        photoWrapper.appendChild(this.cropcontainer);

        this.stack.appendChild(photoWrapper);

        this.animationOverlay = document.createElement('div');
        this.animationOverlay.className = 'mscp-animation-overlay';
        this.stack.appendChild(this.animationOverlay);

        const pp = document.createElement('div');
        pp.className = 'mscp-layer pp';
        this.stack.appendChild(pp);

        const ram = document.createElement('div');
        ram.className = 'mscp-layer ram';
        this.stack.appendChild(ram);

        const giftwrap = document.createElement('div');
        giftwrap.className = 'mscp-layer giftwrap';
        this.stack.appendChild(giftwrap);
    }

    reCreateImg() {
        if (this.img) this.img.remove();

        this.img = document.createElement('img');
        this.img.className = 'mscp-layer photo';
        this.cropcontainer.appendChild(this.img);
    }

    addPreviewContainer() {
        if (!this.previewcontainer) {
            this.previewcontainer = document.createElement('div');
            this.previewcontainer.className = 'preview-container';
            this.cropcontainer.appendChild(this.previewcontainer);
            const previewImg = document.createElement('img');
            previewImg.className = 'mscp-layer img preview';
            previewImg.src = imageUrl('upload_image');
            this.previewcontainer.appendChild(previewImg);
        }
    }

    // loadUploadFunction() {
    //     this.addPreviewContainer();
    //     events.trigger('loadUploadFunction', this.previewcontainer);
    // }

    // triggerUploadFunction() {
    //     this.addPreviewContainer();
    //     events.trigger('triggerUploadFunction', this.previewcontainer);
    // }

    async clearThumbnail() {
        Storage.remove('thumbnailUrl');

        this.wrapper.classList.remove('has-thumbnail');
        const preview = document.getElementById('thumb_preview');
        if (preview) {
            preview.remove();
        }
    }

    async showThumbnail() {
        await new Promise(async resolve => {
            try {

                const thumbnailUrl = Storage.get('thumbnailUrl');
                if (thumbnailUrl) {
                    let preview = document.getElementById('thumb_preview')
                    let img = preview?.querySelector('img');
                    if (!preview) {
                        preview = document.createElement('div');
                        preview.id = 'thumb_preview';
                        this.wrapper.appendChild(preview);
                    }
                    preview.classList.add('loading');
                    if (!img) {
                        img = new Image();
                        preview?.appendChild(img);
                        img.decoding = 'async';
                        img.loading = 'eager';
                    }
                    img.addEventListener('load', () => {
                        this.wrapper.classList.add('has-thumbnail');
                        preview.classList.remove('loading');
                        Logger.debug('Thumbnail loaded successfully');
                        events.trigger('thumbnailUpdated', img.src);
                        resolve();
                    }, { once: true });

                    img.addEventListener('error', (e) => {
                        this.wrapper.classList.remove('has-thumbnail');
                        preview.remove();
                        // Logger.error('Could not load thumbnail', e);
                        // TODO: ska denna visas??
                        // CoverDialog.show({
                        //     type: 'thumbnailLoadFailed',
                        //     data: { errorMessage: 'Could not load thumbnail. Please try again.' }
                        // });
                        console.error('Could not load thumbnail:', e);
                        resolve();
                    }, { once: true });

                    img.src = thumbnailUrl;

                }
            }
            catch (e) {
                console.error(e);
                reject(e);
            }
        });
    }

    async addSaveThumbnailEvents() {
        events.add('preAdjustModeToggled', async (toggle = null) => {
            this.cleanupAnimation();
            if (toggle === false) {
                await this.applyCropBox();
                // await this.uploadAndSaveThumbnail();
            }
        });
        // events.add('cropBoxUpdated', async () => {
        events.add('cropSizeUpdated', async () => {
            await this.applyCropBox();
        });
        // events.add('cropSizeUpdated', async () => {
        //     console.log('cropSizeUpdated - save thumbnail');
        //     await this.uploadAndSaveThumbnail();
        // });

        // only save thumbnail when entering retouch step and not on every cropBox update
        // events.add('preStepChange', async (currentStep, newStep) => {
        //     if (currentStep === 'size' && newStep === 'retouch') { // save thumbnail when entering retouch step
        //         await this.uploadAndSaveThumbnail();
        //     }
        // });
        events.add('uploadAndSaveThumbnail', async (currentStep, newStep) => {
            await this.uploadAndSaveThumbnail();
        });
        events.add('preAdjustModeToggled', async (newState) => {
            if (newState) {
                // show 1:1 overlay animation to help user understand cropping
                this.playOneTimeAnimation();
            }
            else {
                // await this.uploadAndSaveThumbnail();
                this.cleanupAnimation();
            }
        });
    }

    /**
     * A stale image (uploaded before the #507 write-token update) has an id but
     * no token, so the server rejects thumbnail/clone writes with 403. Drop the
     * stale references and send the customer back to the upload step with a
     * message, so they re-upload once instead of looping.
     */
    restartUploadForStaleImage(staleId) {
        Logger.log('Stale image without write token — restarting upload', { staleId });
        try {
            Storage.remove('activeImageID');
            Storage.remove('thumbnailUrl');
            Storage.save('thumbnailUrlDirty', true);
        } catch (e) {
            Logger.error('Failed clearing stale image refs', e);
        }
        // English source doubles as the translation key (delivered .po supplies Swedish).
        CoverDialog.show({
            type: 'uploadFailed',
            data: { errorMessage: _('Your image needs to be uploaded again. Please upload your photo once more.') },
            onClose: () => events.trigger('changeStep', 'upload'),
        });
        events.trigger('changeStep', 'upload');
    }

    async uploadAndSaveThumbnail() {
        try {
            this.cleanupAnimation();

            const isDirty = Storage.get('thumbnailUrlDirty');
            const hasThumbnail = Storage.get('thumbnailUrl');

            // FIX: Om vi saknar tumnagel måste vi skapa en, även om isDirty är false
            if (isDirty === false && hasThumbnail) {
                Logger.debug('Thumbnail is not dirty and exists, skipping upload');
                return;
            }

            events.trigger('setPopupHold', 1); // Hold popup
            Logger.debug('uploadAndSaveThumbnail');

            const activeImageID = Storage.get('activeImageID');

            // If editing from cart, clone the files to a new ID before generating thumbnail
            if (activeImageID) {
                const newImageId = Storage.generateSessionId(); // Generate new random ID for images

                try {
                    Logger.log('Edit mode detected: Cloning files to new ID', {
                        oldId: activeImageID,
                        newId: newImageId,
                    });

                    // Call API to clone files from old ID to new ID
                    const response = await fetch('/wp-json/mscp/v1/clone_files', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            old_id: activeImageID,
                            new_id: newImageId,
                            token: imageTokens.get(activeImageID),
                        }),
                    });

                    // An image uploaded before the #507 update has an id in
                    // localStorage but no write token, so clone_files returns 403.
                    // Send the customer back to re-upload instead of looping.
                    if (response.status === 403 && !imageTokens.has(activeImageID)) {
                        this.restartUploadForStaleImage(activeImageID);
                        events.trigger('setPopupHold', 0);
                        return;
                    }

                    const result = await response.json();

                    if (result.success) {
                        imageTokens.set(newImageId, result.token);
                        await db.cloneImageMeta(activeImageID, newImageId);
                        // Update activeImageID to the new cloned ID
                        Storage.save('activeImageID', newImageId);
                        Logger.log('Files cloned successfully for edit mode', {
                            oldId: activeImageID,
                            newId: newImageId,
                            copiedFiles: result.copied_files
                        });
                    } else {
                        console.error('Failed to clone files for edit mode', result);
                        CoverDialog.show({
                            type: 'cloneFailed',
                            data: { oldId: activeImageID }
                        });
                        // Continue anyway - thumbnail will be generated with old ID if cloning fails
                    }
                } catch (error) {
                    console.error('Error cloning files for edit mode', error);
                    CoverDialog.show({
                        type: 'cloneFailed',
                        data: { oldId: activeImageID }
                    });
                    // Continue anyway - thumbnail will be generated with current ID if error occurs
                }
            }

            // Kontrollera om vi fortfarande visar en Blob-bild trots att servern är klar.
            const currentActiveImageID = Storage.get('activeImageID');
            if (currentActiveImageID && this.img && this.img.src.startsWith('blob:')) {
                // Om så är fallet, tvinga en uppdatering till server-URL innan vi ritar tumnageln.
                const imgData = await db.getImageData(currentActiveImageID);
                if (imgData && imgData.serverUrl) {
                    Logger.debug('Thumbnail generation: Blob found but server URL exists. Force updating source.');
                    await this.updateImageSrc(imgData.serverUrl);
                }
            }

            await this.clearThumbnail();

            // check if cropBox is valid
            const cropBox = Storage.get('cropBox');
            if (
                !cropBox ||
                typeof cropBox.left !== 'number' ||
                typeof cropBox.top !== 'number' ||
                typeof cropBox.width !== 'number' ||
                typeof cropBox.height !== 'number'
            ) {
                console.error('Cannot create thumbnail, crop box is not set');
                CoverDialog.show({
                    type: 'invalidCropBox'
                });
                events.trigger('setPopupHold', 0);
                return;
            }

            // Kontrollera om retouch är satt till 'grayscale' eller liknande
            const retouch = Storage.get('retouch')?.replace('retouch-', '');
            const grayscale = retouch === 'grayscale' || retouch === 'bw' || retouch === 'svartvit';
            // Graphic looks: the thumbnail is the drawn graphic (image + the visitor's text), same layout as the print version.
            let graphic = null;
            try {
                graphic = await renderCurrentGraphic(1000);
            } catch (e) {
                Logger.warn('Graphic thumbnail failed, using the plain crop.', e);
            }
            const upload = graphic
                ? await ThumbnailCompositor.uploadCanvas(graphic)
                : await ThumbnailCompositor.uploadImage(this.stack, { scale: 2, background: '#fff', grayscale });

            if (upload?.success && upload?.url) {
                Storage.save('thumbnailUrl', upload.url);
                Logger.debug('Thumbnail uploaded successfully', upload.url);
                Storage.save('thumbnailUrlDirty', false); // Mark thumbnail as not dirty
            } else if (upload?.status === 403 && !imageTokens.has(Storage.get('activeImageID'))) {
                // Pre-#507 image with no write token — re-upload, don't loop back to paper.
                this.restartUploadForStaleImage(Storage.get('activeImageID'));
            } else {
                Logger.error(upload?.error || _('Unknown error uploading thumbnail'));
                events.trigger('changeStep', 'paper'); // Gå tillbaka till paper steg vid fel
            }

            events.trigger('setPopupHold', 0);
        } catch (e) {
            Logger.error(_('Error uploading thumbnail', e));
            events.trigger('setPopupHold', 0);
        }
    }

    /**
     * Uppdaterar <img> src till server URL
     * @param {string} serverUrl - Den nya URL:en från servern.
     */
    /**
     * NY METOD: Uppdaterar <img> src flimmerfritt.
     * Denna metod anropar INTE reCreateImg().
     * Tar nu hänsyn till HEIC-filer som inte har en blob-src.
     * @param {string} serverUrl - Den nya URL:en från servern.
     */
    async updateImageSrc(serverUrl) {
        // 1. Säkerhetskollar
        if (!this.img || !serverUrl) return; // Ingen bild att uppdatera
        if (this.img.src === serverUrl) return; // Redan uppdaterad

        // 2. HANTERA HEIC-FALLET (din observation)
        // Om 'src' är tom, fanns det ingen blob-URL att visa.
        // Då behöver vi inte vara "flimmerfria", vi ska bara
        // ladda bilden för första gången.
        if (!this.img.src) {
            Logger.debug('Image src är tom (HEIC-fall). Laddar server-bild direkt.');
            // this.clearMessages();
            this.img.src = serverUrl;
            // Din befintliga 'this.img.onload' som sattes i 'reCreateImg'
            // kommer nu att köras och trigga 'imageLoaded'.
            return; // Vi är klara här
        }

        // 3. HANTERA NORMALT FLÖDE (JPG/PNG med befintlig blob-URL)
        // Här finns redan en bild och vi vill byta den snyggt.
        Logger.debug('Börjar förladdning av server-bild för flimmerfritt byte...');

        const tempImg = new Image();
        tempImg.src = serverUrl;

        try {
            // Vänta tills den osynliga bilden är helt laddad
            await new Promise((resolve, reject) => {
                tempImg.onload = resolve;
                tempImg.onerror = reject;
            });

            // 4. Byt källan på den synliga bilden
            this.img.src = serverUrl;
            Logger.debug('Image SRC uppdaterad till server URL (flimmerfritt).');

        } catch (error) {
            Logger.debug('Misslyckades med att förladda server-bild:', error);
            // Felfall: Byt källan ändå. Det kan blinka, men bilden byts ut.
            this.img.src = serverUrl;
        }
    }
    // async updateImageSrc(serverUrl) {
    //     // 1. Säkerhetskollar
    //     if (!this.img || !serverUrl) return; // Ingen bild att uppdatera
    //     if (this.img.src === serverUrl) return; // Redan uppdaterad

    //     Logger.debug('Börjar förladdning av server-bild för flimmerfritt byte...');

    //     // 2. Ladda den nya bilden osynligt i bakgrunden
    //     const tempImg = new Image();
    //     tempImg.src = serverUrl;

    //     try {
    //         // Vänta tills den osynliga bilden är helt laddad
    //         await new Promise((resolve, reject) => {
    //             tempImg.onload = resolve;
    //             tempImg.onerror = reject;
    //         });

    //         // 3. Först NU, byt källan på den synliga bilden
    //         // Eftersom den redan finns i webbläsarens cache blir bytet omedelbart.
    //         this.img.src = serverUrl;
    //         Logger.debug('Image SRC uppdaterad till server URL (flimmerfritt).');

    //     } catch (error) {
    //         Logger.error('Misslyckades med att förladda server-bild:', error);
    //         // Felfall: Byt källan ändå. Det kan blinka, men bilden byts ut.
    //         this.img.src = serverUrl;
    //     }
    // }

    /**
     * Load `url` into a fresh this.img element. Resolves true once the image
     * has loaded, false if it errors. Never rejects — the caller decides what
     * a failure means.
     */
    _tryLoadImage(url) {
        this.reCreateImg();
        return new Promise((resolve) => {
            this.img.onload = () => {
                this.img.width = this.img.naturalWidth;
                this.img.height = this.img.naturalHeight;
                resolve(true);
            };
            this.img.onerror = () => resolve(false);
            this.img.src = url;
        });
    }

    /**
     * Resolve an uploaded image's display-ready URL from the server using
     * only its id. This is how the customizer recovers its image after a
     * page reload — when the in-memory blob and the session url are gone,
     * the uploaded file still lives on the server as {id}_copy.jpg.
     * Returns the URL, or null if the server has no usable copy.
     */
    async fetchServerImageUrl(id) {
        if (!id) return null;
        try {
            const body = new FormData();
            body.append('id', id);
            const res = await fetch('/wp-json/mscp/v1/check_image', { method: 'POST', body });
            if (!res.ok) return null;
            const data = await res.json();
            return data?.copy_url || null;
        } catch (e) {
            console.error('check_image rehydration failed:', e);
            return null;
        }
    }

    async loadActiveImage() {
        Logger.debug('loadActiveImage');
        const image = await imageHelper.getActiveImage();
        const activeImageID = Storage.get('activeImageID');

        // Nothing to load at all — show the upload UI.
        if (!image && !activeImageID) {
            this.reCreateImg();
            Logger.debug('No active image found, loading upload function');
            await this.initUpload();
            return 'No image loaded';
        }

        // 1. Try the locally-known url first (blob during upload, or serverUrl).
        if (image?.url) {
            if (await this._tryLoadImage(image.url)) {
                document.querySelector('.mscp-dropzone')?.remove();
                events.trigger('imageLoaded');
                Logger.debug('Image loaded:', image.image?.name);
                return 'Image loaded successfully';
            }
            console.error('Local image url failed to load, attempting server recovery:', activeImageID);
        }

        // 2. Genuinely still converting (HEIC, no server copy yet) — wait,
        //    don't fall through to recovery or the upload flow.
        if (image && !image.url && image.image?.uploadStatus === 'converting') {
            this.reCreateImg();
            Logger.debug('HEIC image converting, waiting for conversion...');
            return 'Image is converting';
        }

        // 3. Recover from the server using just the image id. This is the
        //    post-reload path: the in-memory blob / session url is gone, but
        //    the uploaded file still lives on the server as {id}_copy.jpg.
        const serverUrl = await this.fetchServerImageUrl(activeImageID);
        if (serverUrl && await this._tryLoadImage(serverUrl)) {
            // Re-acquire into the session so later steps and the next reload
            // resolve instantly without another round-trip.
            await db.updateImageData(activeImageID, {
                uploadStatus: 'uploaded',
                serverUrl: serverUrl
            }).catch(() => {});
            document.querySelector('.mscp-dropzone')?.remove();
            events.trigger('imageLoaded');
            Logger.debug('Image recovered from server:', activeImageID);
            return 'Image loaded successfully (recovered)';
        }

        // 4. Nothing loaded — recover gracefully via the upload flow. A
        //    missing image is a re-upload prompt, not a crash: console.error
        //    (no red banner) and no unhandled promise rejection.
        console.error('Customizer image unavailable, reopening upload:', activeImageID);
        this.reCreateImg();
        await this.initUpload();
        return 'Image unavailable, upload reopened';
    }

    // addMessage(type, message) {
    //     this.clearMessages();
    //     const messageWrapper = document.createElement('div');
    //     messageWrapper.className = 'mscp-info-messages';
    //     messageWrapper.classList.add(type);
    //     // messageWrapper.addEventListener('click', () => {
    //     //     messageWrapper.remove();
    //     // });
    //     const messageParagraph = document.createElement('p');
    //     messageParagraph.classList.add(type);
    //     messageParagraph.innerHTML = message;
    //     messageWrapper.appendChild(messageParagraph);
    //     this.wrapper.appendChild(messageWrapper);
    // }
    // clearMessages() {
    //     this.wrapper.querySelectorAll('.error').forEach(el => el.remove());
    //     this.wrapper.querySelectorAll('.info').forEach(el => el.remove());
    // }

    async updateOnLoad() {
        await this.loadActiveImage();
        if (this.options.triggerDropzoneWhenReady) {
            await this.initUpload(true); // Trigger click to show upload immediately
        }
    }

    async updateCropSize() {
        Logger.debug('updateCropSize');

        if (!this.stack || !this.img || this.img.src === '' || this.img.naturalWidth === 0 || this.img.naturalHeight === 0) {
            Logger.debug('Image not initialized');
            return;
        }
        let size = Storage.get('size') || window.mscp?.defaults?.size || '40x50';
        let presentation = Storage.get('presentation') || 'empty';
        const orientation = Storage.get('orientation') || window.mscp?.defaults?.orientation || 'portrait';
        const paper = Storage.get('paper');

        // Track whether presentation has been selected at least once (for visual fallback)
        if (presentation && presentation !== 'empty') {
            this.hasHadPresentation = true;
        }

        // If presentation was cleared on paper change, use first valid as visual fallback
        // Affects dataset + crop calculation but is NOT saved to Storage
        if (presentation === 'empty' && paper && this.hasHadPresentation) {
            const validPresentations = await products.getAttributeHierarchyPresentations();
            if (validPresentations.length >= 1) {
                presentation = validPresentations[0];
            }
        }

        // Validate that size exists for the current paper > presentation combo
        if (paper && presentation && presentation !== 'empty') {
            const validProductIDs = await products.getAttributeHierarchy(paper, presentation);
            if (validProductIDs.length > 0) {
                const allProducts = await products.getProducts();
                const validSizes = [...new Set(
                    validProductIDs.flatMap(prodId => {
                        const prod = allProducts.find(p => p.value === prodId);
                        return prod?.variations?.map(v => v.attributes?.attribute_pa_size).filter(Boolean) || [];
                    })
                )];
                if (validSizes.length > 0 && !validSizes.includes(size)) {
                    size = validSizes[0];
                    Logger.debug('Size %s not available for %s > %s, falling back to %s', Storage.get('size') || '40x50', paper, presentation, size);
                }
            }
        }

        this.stack.dataset.orientation = orientation;
        this.stack.dataset.presentation = presentation;
        this.stack.dataset.size = size;
        if (paper) {
            this.stack.dataset.paper = paper;
        }

        // Uppdatera frame baserat pa paper/presentation (default fran admin)
        const frame = await products.getDefaultFrame(paper, presentation);
        if (frame) {
            this.stack.dataset.frame = frame;
        } else {
            delete this.stack.dataset.frame;
        }

        this.size = size;

        // skip transform if presentation is empty
        if (presentation === 'empty') {
            this.aspectRatio = 'auto';
            this.cropcontainer.style.aspectRatio = 'auto';
            this.img.style.transform = 'none';
        }
        else {
            const resolution = await products.getSizeResolution(size, presentation, orientation, paper);
            const [wMm, hMm, bleedMm] = resolution || [0, 0, 0];
            this.bleedMm = bleedMm || 0;
            this.printWMm = wMm;
            this.printHMm = hMm;
            if (!wMm || !hMm) {
                // Visa CoverDialog direkt
                CoverDialog.show({
                    type: 'invalidResolution',
                    data: { size, presentation, orientation }
                });
                this.aspectRatio = 'auto';
                this.cropcontainer.style.aspectRatio = 'auto';
                this.img.style.transform = 'none';
                return;
            }

            this.aspectRatio = wMm / hMm;
            this.cropcontainer.style.aspectRatio = `${wMm} / ${hMm}`;
            // const parentSquareBounding = this.stack.getBoundingClientRect();
            // const containerWidth = parentSquareBounding.width;
            // this.cropcontainer.style.width = `${containerWidth}px`;
            // const calcVisualCropSize = imageHelper.getVisualCropSize(wMm, hMm);
            this.resetTransform();
            events.trigger('cropSizeUpdated', { aspectRatio: this.aspectRatio });
        }
    }


    /**
     * Help: Calculate scale and offset (px) based on cropBox
     * @param {{left:number, top:number, width:number, height:number}} cropBox 
     * @returns {{scale:number, offsetX:number, offsetY:number}}
     */
    getTransformFromBox(cropBox) {
        const containerRect = this.cropcontainer.getBoundingClientRect();
        const { left, top, width, height } = cropBox;
        // scale so that cropBox width fills the container
        const scale = containerRect.width / (width * this.img.naturalWidth);
        // calculate offset in px
        const offsetX = -left * scale * this.img.naturalWidth;
        const offsetY = -top * scale * this.img.naturalHeight;
        return { scale, offsetX, offsetY };
    }

    /**
     * resetTransform
     * @returns {void}
     */
    async resetTransform() {
        const containerRect = this.cropcontainer.getBoundingClientRect();
        // if container is zero sized, check parent
        if (containerRect.width === 0 || containerRect.height === 0) {
            const parentRect = this.cropcontainer.parentElement.getBoundingClientRect();
            if (parentRect.width > 0 && parentRect.height > 0) {
                containerRect.width = parentRect.width;
                containerRect.height = parentRect.height;
            }
        }

        this.minScale = Math.max(
            containerRect.width / this.img.naturalWidth,
            containerRect.height / this.img.naturalHeight
        );

        if (this.minScale === 0 || isNaN(this.minScale) || !isFinite(this.minScale)) {
            Logger.debug(`Failed to calculate minScale. Container: ${containerRect.width}x${containerRect.height}, Image: ${this.img.naturalWidth}x${this.img.naturalHeight}`);
            // Sätt en fallback för att undvika NaN-fel
            this.minScale = 0.1;
        }

        // Bleed-begransning: hojer minScale sa att bilden alltid
        // tacker print + bleed (omvik) runtom synliga ytan
        if (this.bleedMm > 0 && this.printWMm && this.printHMm) {
            const totalW = this.printWMm + (this.bleedMm * 2);
            const totalH = this.printHMm + (this.bleedMm * 2);
            const ratio = Math.min(this.printWMm / totalW, this.printHMm / totalH);
            this.minScale = this.minScale / ratio;
        }

        // Dynamically set maxScale as a multiple of minScale (efter bleed-justering)
        const maxZoom = parseFloat(window?.mscp?.settings?.maxZoom);
        if (!isNaN(maxZoom)) {
            this.maxScale = this.minScale * maxZoom;
        } else {
            this.maxScale = this.minScale * 3;
        }

        let cropBox = Storage.get('cropBox');
        if (
            !cropBox ||
            typeof cropBox.left !== 'number' || isNaN(cropBox.left) ||
            typeof cropBox.top !== 'number' || isNaN(cropBox.top) ||
            typeof cropBox.width !== 'number' || isNaN(cropBox.width) ||
            typeof cropBox.height !== 'number' || isNaN(cropBox.height)
        ) {
            cropBox = { left: 0, top: 0, width: 1, height: 1 };
            this.scale = this.minScale;


            // if (forceNewCrop) {
            //     // This block handles new image uploads

            //     // Use our new, reliable centering logic
            await this.reCenter();
        } else {
            // Adjust cropBox to new aspectRatio
            cropBox = this.adjustCropBoxToNewAspectRatio(cropBox, this.aspectRatio);

            const { scale, offsetX, offsetY } = this.getTransformFromBox(cropBox);
            this.scale = Math.max(scale, this.minScale);

            if (scale < this.minScale) {
                this.pos.x = -cropBox.left * this.scale * this.img.naturalWidth;
                this.pos.y = -cropBox.top * this.scale * this.img.naturalHeight;
            } else {
                this.pos.x = offsetX;
                this.pos.y = offsetY;
            }
        }

        this.applyTransform();
    }

    async reCenter() {
        const containerRect = this.cropcontainer.getBoundingClientRect();
        const scaledImgWidth = this.img.naturalWidth * this.scale;
        const scaledImgHeight = this.img.naturalHeight * this.scale;

        // Calculate the position to center the image
        this.pos.x = (containerRect.width - scaledImgWidth) / 2;
        this.pos.y = (containerRect.height - scaledImgHeight) / 2;
    }

    /**
     * applyTransform updates CSS, clips to boundaries and saves cropBox
     * @returns {void}
     */
    applyTransform() {
        const limit = this.getLimits();
        this.pos.x = Math.min(limit.maxX, Math.max(limit.minX, this.pos.x));
        this.pos.y = Math.min(limit.maxY, Math.max(limit.minY, this.pos.y));
        // apply CSS
        this.img.style.transform = `translate(${this.pos.x}px, ${this.pos.y}px) scale(${this.scale})`;

    }
    async applyCropBox() {
        this.applyTransform();

        // save cropBox
        const cropBox = this.getCropBox();

        // don't save cropBox if aspectRatio is 'auto'
        if (cropBox && cropBox.aspectRatio && cropBox.aspectRatio !== 'auto') {
            await Storage.save('cropBox', this.getCropBox());
            Logger.debug('Saved cropBox:', Storage.get('cropBox'));
            events.trigger('cropBoxUpdated');
        }
        else {
            Storage.remove('cropBox');
            Logger.debug('Aspect ratio is auto, not saving cropBox');
        }
    }

    adjustCropBoxToNewAspectRatio(cropBox, newAspectRatio) {
        const currentAspect = cropBox.width / cropBox.height;
        if (Math.abs(currentAspect - newAspectRatio) < 0.001) {
            return cropBox;
        }

        let newWidth = cropBox.width;
        let newHeight = cropBox.height;

        if (newAspectRatio > currentAspect) {
            // Increase width, keep top
            newWidth = cropBox.height * newAspectRatio;
        } else {
            // Increase height, keep left
            newHeight = cropBox.width / newAspectRatio;
        }

        // Limit so it doesn't go outside the image
        const maxWidth = 1 - cropBox.left;
        const maxHeight = 1 - cropBox.top;
        newWidth = Math.min(newWidth, maxWidth);
        newHeight = Math.min(newHeight, maxHeight);

        return {
            left: cropBox.left,
            top: cropBox.top,
            width: newWidth,
            height: newHeight
        };
    }

    getLimits() {
        const containerRect = this.cropcontainer.getBoundingClientRect();
        const imgW = this.img.naturalWidth * this.scale;
        const imgH = this.img.naturalHeight * this.scale;
        const visibleW = containerRect.width;
        const visibleH = containerRect.height;

        // If the image is narrower than the container, (visibleW - imgW) will be positive.
        // If the image is wider, it will be negative.
        // Math.min/max will automatically sort these to the correct limits.
        const minX = Math.min(0, visibleW - imgW);
        const maxX = Math.max(0, visibleW - imgW);
        const minY = Math.min(0, visibleH - imgH);
        const maxY = Math.max(0, visibleH - imgH);

        return { minX, maxX, minY, maxY };
    }

    zoom(delta) {
        const newScale = this.scale + delta;

        // Limit new scale between minScale and maxScale
        if (newScale < this.minScale) {
            this.scale = this.minScale;
            return; // nothing to do, already too small
        }
        if (newScale > this.maxScale) {
            this.scale = this.maxScale;
            return; // nothing to do, already too large
        }
        this.doZoom(newScale);
    }

    doZoom(newScale) {
        // Zoom towards center of crop container
        const centerX = this.cropcontainer.offsetWidth / 2;
        const centerY = this.cropcontainer.offsetHeight / 2;

        const scaleChange = newScale / this.scale;

        // const rect = this.cropcontainer.getBoundingClientRect();
        const offsetX = centerX - this.pos.x;
        const offsetY = centerY - this.pos.y;

        this.pos.x -= offsetX * (scaleChange - 1);
        this.pos.y -= offsetY * (scaleChange - 1);

        // new scale
        this.scale = newScale;

        this.applyCropBox();
    }

    checkIfInAdjustMode() {
        return this.cropcontainer.closest('.mscp-popup-content').classList.contains('adjust-mode');
    }

    attachEvents() {
        this.wrapper.addEventListener('mousedown', e => {
            if (!this.checkIfInAdjustMode()) return;
            this.dragging = true;
            this.start = {
                x: e.clientX,
                y: e.clientY,
                offsetX: this.pos.x,
                offsetY: this.pos.y
            };
            e.preventDefault();
        });

        window.addEventListener('mousemove', e => {
            if (!this.checkIfInAdjustMode()) return;
            if (!this.dragging) return;
            const dx = e.clientX - this.start.x;
            const dy = e.clientY - this.start.y;
            this.pos.x = this.start.offsetX + dx;
            this.pos.y = this.start.offsetY + dy;
            this.applyTransform();
        });

        window.addEventListener('mouseup', () => {
            if (!this.checkIfInAdjustMode()) return;
            if (!this.dragging) return;
            this.dragging = false;
            this.applyCropBox();
        });

        this.wrapper.addEventListener('wheel', e => {
            if (!this.checkIfInAdjustMode()) return;
            e.preventDefault();
            this.zoom(-e.deltaY * 0.001);
        }, { passive: false });


        this.wrapper.addEventListener('touchstart', e => {
            if (!this.checkIfInAdjustMode()) return;
            if (e.touches.length === 1) {
                this.dragging = true;
                this.start = {
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY,
                    offsetX: this.pos.x,
                    offsetY: this.pos.y
                };
            } else if (e.touches.length === 2) {
                this.pinchStart = {
                    dist: this.getDistance(e.touches),
                    scale: this.scale
                };
            }
        });

        this.wrapper.addEventListener('touchmove', e => {
            if (!this.checkIfInAdjustMode()) return;
            if (e.touches.length === 1 && this.dragging) {
                const dx = e.touches[0].clientX - this.start.x;
                const dy = e.touches[0].clientY - this.start.y;
                this.pos.x = this.start.offsetX + dx;
                this.pos.y = this.start.offsetY + dy;
                this.applyTransform();
            } else if (e.touches.length === 2 && this.pinchStart) {
                const newDist = this.getDistance(e.touches);
                const delta = (newDist - this.pinchStart.dist) * 0.0005; // sensitivity factor

                // const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                // const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

                this.zoom(delta);

                this.pinchStart.dist = newDist;
            }
        });

        this.wrapper.addEventListener('touchend', (e) => { // Pass the event object 'e'
            if (!this.checkIfInAdjustMode()) return;
            if (this.dragging) {
                this.applyCropBox(); // Spara slutligt panoreringsläge
            }
            if (e.touches.length < 1) {
                this.dragging = false;
            }
            if (e.touches.length < 2) {
                this.pinchStart = null;
            }
        });
    }

    getDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    playOneTimeAnimation() {
        if (!this.animationOverlay) return;

        // Remove old animation if it exists
        this.cleanupAnimation();

        const animation = document.createElement('img');
        const url = imageUrl('upload11_overlay_animation'); // URL to GIF
        if (!url) {
            Logger.debug('No overlay animation URL found.');
            return;
        }
        animation.src = url;
        animation.className = 'one-time-gif';
        animation.addEventListener('click', () => {
            // allow user to click to dismiss
            this.animationOverlay.classList.remove('active');
            events.trigger('toggleAdjustMode', true); // make sure adjust mode is on
            setTimeout(() => {
                animation.remove();
            }, 500); // Give time for any CSS transition to complete
        });
        animation.addEventListener('error', () => {
            Logger.debug('Error loading overlay animation image.');
        });

        // Use onload to ensure the image is loaded before displaying it
        animation.onload = () => {
            this.animationOverlay.appendChild(animation);
            this.animationOverlay.classList.add('active');
            const gifDuration = getNumber('upload11_overlay_animation_length', 2000); // default 2000 ms if not specified

            setTimeout(() => {
                this.animationOverlay.classList.remove('active');
                setTimeout(() => {
                    animation.remove();
                }, 500); // Give time for any CSS transition to complete
            }, gifDuration);
        };
    }

    cleanupAnimation() {
        if (this.animationOverlay) {
            this.animationOverlay.classList.remove('active');
            this.animationOverlay.innerHTML = '';
        }
    }

    getCropBox() {
        const containerRect = this.cropcontainer.getBoundingClientRect();
        const imgW = this.img.naturalWidth * this.scale;
        const imgH = this.img.naturalHeight * this.scale;

        const left = -this.pos.x / imgW;
        const top = -this.pos.y / imgH;
        const width = containerRect.width / imgW;
        const height = containerRect.height / imgH;

        const aspectRatio = (this.aspectRatio === 'auto') ? this.aspectRatio : this.aspectRatio.toFixed(4);

        return {
            left: parseFloat(left.toFixed(8)),
            top: parseFloat(top.toFixed(8)),
            width: parseFloat(width.toFixed(8)),
            height: parseFloat(height.toFixed(8)),
            aspectRatio: parseFloat(aspectRatio)
        };
    }
}

export default CropImageViewport;